/**
 * CSRF token handling for requests.
 * Cookie mode: token lives in backend `XSRF-TOKEN` cookie (`withCredentials`).
 * Never persist CSRF in sessionStorage / localStorage in cookie mode.
 *
 * Token is read from (in order):
 * 1. Cookie: XSRF-TOKEN or csrf_token
 * 2. In-memory (same tab only, from login JSON — JS cannot read cross-origin API cookies)
 * 3. localStorage: only when cookie mode is off (legacy)
 */

import { AUTH_USE_HTTPONLY_COOKIE } from '../config/authFlags';

const CSRF_COOKIE_NAMES = ['XSRF-TOKEN', 'csrf_token', 'csrf-token'];
const CSRF_STORAGE_KEY = 'csrf_token';
const CSRF_HEADER_NAME = 'X-CSRF-Token'; // or 'X-XSRF-TOKEN' if backend expects that

/** Same-tab only. Cookie mode does not write this to sessionStorage. */
let memoryCsrfToken = null;

function removeCsrfFromJsStorage() {
  try {
    sessionStorage.removeItem(CSRF_STORAGE_KEY);
    localStorage.removeItem(CSRF_STORAGE_KEY);
  } catch (_) {
    // ignore
  }
}

/** Strip leftover `csrf_token` from Session Storage (cookie mode = cookie only). */
export function purgeStoredCsrfIfCookieMode() {
  if (!AUTH_USE_HTTPONLY_COOKIE) return;
  removeCsrfFromJsStorage();
}

/**
 * Get a cookie value by name.
 * @param {string} name - Cookie name
 * @returns {string|null}
 */
function getCookie(name) {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[2].trim()) : null;
}

/**
 * Returns the CSRF token from cookie first, then same-tab memory.
 * Cookie mode never reads sessionStorage / localStorage.
 * @returns {string|null}
 */
export function getCsrfToken() {
  for (const name of CSRF_COOKIE_NAMES) {
    const value = getCookie(name);
    if (value) return value;
  }
  if (memoryCsrfToken) return memoryCsrfToken;
  if (AUTH_USE_HTTPONLY_COOKIE) {
    return null;
  }
  try {
    const stored = localStorage.getItem(CSRF_STORAGE_KEY);
    if (stored && stored.trim() !== '') return stored.trim();
  } catch (_) {
    // ignore
  }
  return null;
}

/**
 * Cookie mode: memory only (login body). Cookie is set by API (`XSRF-TOKEN`).
 * Legacy mode may keep a localStorage copy.
 * @param {string} token
 */
export function setCsrfToken(token) {
  if (!token || typeof token !== 'string') return;
  memoryCsrfToken = token.trim();
  if (AUTH_USE_HTTPONLY_COOKIE) {
    removeCsrfFromJsStorage();
    return;
  }
  try {
    localStorage.setItem(CSRF_STORAGE_KEY, token.trim());
  } catch (_) {
    // ignore
  }
}

/**
 * Clear stored CSRF token (e.g. on logout).
 */
export function clearCsrfToken() {
  memoryCsrfToken = null;
  removeCsrfFromJsStorage();
}

/**
 * Keep local CSRF in sync when API rotates token (production per-request rotation).
 * @param {import('axios').AxiosResponse} response
 */
export function syncCsrfFromResponse(response) {
  if (!response) return;
  const headers = response.headers || {};
  const fromHeader =
    headers['x-new-csrf-token'] ||
    headers['x-csrf-token'] ||
    headers['X-New-CSRF-Token'] ||
    headers['X-CSRF-Token'];
  const fromBody =
    response.data?.newCsrfToken ||
    response.data?.csrfToken;
  const next = fromHeader || fromBody;
  if (next && String(next).trim()) {
    setCsrfToken(String(next).trim());
  }
}

export { CSRF_HEADER_NAME };
